import { Component, inject, OnInit } from '@angular/core';
import { ProductionBatchState } from "../../../store/batch/batch.state";
import { BatchFilterOptions, Color, Lab, PoState, poStateTranslation, ProductionBatch } from "../../../model";
import { NzTableModule } from "ng-zorro-antd/table";
import { animate, style, transition, trigger } from "@angular/animations";
import data from 'src/assets/baseMap.json';
import { toUpper } from "lodash";
import { CommonModule, DatePipe, DecimalPipe, I18nSelectPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { PipesModule } from '../../pipes/pipes.module';
import { ComponentsModule } from '../components.module';
import { BackendService } from '../../../services/backend';
import { finalize } from 'rxjs';

interface FilterOption {
	text: string;
	value: string;
}

// Mirrors backend NumericFilterOperator (Model/BatchQueryRequest.cs) — wire values are the
// enum member names verbatim (ASP.NET Core's global JsonStringEnumConverter, PascalCase, no naming policy).
export type NumericOperator = 'Equal' | 'GreaterThan' | 'GreaterThanOrEqual' | 'LessThan' | 'LessThanOrEqual' | 'Between';

interface NumericFilterValue {
	operator: NumericOperator;
	value: number | null;
	// Only used when operator === 'Between' (inclusive upper bound).
	valueTo: number | null;
}

interface BatchQueryPayload {
	pageIndex: number;
	pageSize: number;
	filters: Record<string, string>;
	multiFilters: Record<string, string[]>;
	dateRangeFilters: Record<string, { from?: string; to?: string }>;
	numericFilters: Record<string, { operator: NumericOperator; value: number; valueTo?: number }>;
	sortKey?: string;
	sortDirection?: number;
}

@Component({
	selector: 'app-sort-filter-work-batches',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		RouterLink,
		NzTableModule,
		NzGridModule,
		NzInputModule,
		NzIconModule,
		NzTooltipModule,
		NzDropdownModule,
		NzDatePickerModule,
		NzButtonModule,
		NzSelectModule,
		NzInputNumberModule,
		I18nSelectPipe,
		DecimalPipe,
		DatePipe,
		PipesModule,
		ComponentsModule
	],
	providers: [DatePipe],
	templateUrl: './sort-filter-work-batches.component.html',
	animations: [
		trigger('highlight', [
			transition(':increment', [
				style({ backgroundColor: 'lightgreen' }),
				animate('3000ms', style({ backgroundColor: 'undefined' })),
			])
		])
	]
})
export class SortFilterWorkBatchesComponent implements OnInit {
	private backend = inject(BackendService);
	private message = inject(NzMessageService);
	private datePipe = inject(DatePipe);

	data: any = data;

	PoState = PoState;
	poStateTranslation = poStateTranslation;
	virtualItemSize: number = 55;
	pageSize = 50;

	filters = {
		poName: '',
		colorCode: '',
		// dEAvg/dEMax/ci are computed (NotMapped) columns — the backend never wired these
		// into a real SQL filter (see ProductionOrderService.GetFilteredAndSorted). Left as
		// plain no-op inputs, unchanged, out of scope for the numeric range/condition upgrade.
		dEAvg: '',
		dEMax: '',
		ci: ''
	};

	// Integer/double columns that are real DB-mapped ProductionBatch properties — these get the
	// operator (=, >, >=, <, <=, between) + value filter UI, translated into SQL server-side
	// (ProductionOrderService.ApplyNumericFilter). Mirrors backend BatchQueryRequest.NumericFilters.
	readonly numericColumns = [
		'inspectionSequence', 'producingQuantity', 'orderedQuantity', 'remainingQuantity',
		'totalRemainingQuantity', 'inspectedQuantity', 'totalInspectedQuantity', 'failedQuantity'
	] as const;

	numericFilters: Record<string, NumericFilterValue> = Object.fromEntries(
		this.numericColumns.map(key => [key, { operator: 'Equal' as NumericOperator, value: null, valueTo: null }])
	);
	numericFilterVisible: Record<string, boolean> = Object.fromEntries(
		this.numericColumns.map(key => [key, false])
	);

	// Excel-style multi-select checkbox filters. Options are fetched once and cached in
	// BackendService (shareReplay) rather than re-queried on every keystroke/open.
	scannerIdOptions: FilterOption[] = [];
	baseMaterialOptions: FilterOption[] = [];
	private selectedScannerIds: string[] = [];
	private selectedBaseMaterialIds: string[] = [];

	// PoState is a fixed enum, so its filter options don't need a backend round-trip.
	stateOptions: FilterOption[] = Object.values(PoState).map(s => ({ text: poStateTranslation[s], value: s }));
	private selectedStates: string[] = [];

	modifiedTimeRange: Date[] | null = null;
	// Default view: last 2 months, most recently started first. The user can clear/change both.
	startedTimeRange: Date[] | null = SortFilterWorkBatchesComponent.defaultStartedTimeRange();
	modifiedTimeFilterVisible = false;
	startedTimeFilterVisible = false;

	isExporting = false;

	sortMap: { [key: string]: 'ascend' | 'descend' | null } = { startedTime: 'descend' };

	// Survives this component being destroyed/recreated when the tab is closed and reopened
	// (nz-tabs keeps activated tabs alive, but leaving/returning to the Home page itself
	// destroys and recreates it) — avoids re-hitting POST /batches/filter on every reopen
	// once the default view has already been loaded once this session.
	private static initialLoadDone = false;

	constructor(public state: ProductionBatchState) {}

	private static defaultStartedTimeRange(): Date[] {
		const to = new Date();
		const from = new Date(to);
		from.setMonth(from.getMonth() - 2);
		return [from, to];
	}

	ngOnInit(): void {
		this.backend.getBatchFilterOptions().subscribe((options: BatchFilterOptions) => {
			this.scannerIdOptions = options.scannerIds.map(id => ({ text: id, value: id }));
			this.baseMaterialOptions = options.baseMaterialIds.map(id => ({
				text: this.data[id.toString()] || id.toString(),
				value: id.toString()
			}));
		});
		if (!SortFilterWorkBatchesComponent.initialLoadDone || !this.state.allLoaded.length) {
			SortFilterWorkBatchesComponent.initialLoadDone = true;
			this.dispatchQuery();
		}
	}

	get filteredAndSortedBatches(): ProductionBatch[] {
		return this.state.allLoaded;
	}

	get modifiedTimeFilterActive(): boolean {
		return !!(this.modifiedTimeRange && (this.modifiedTimeRange[0] || this.modifiedTimeRange[1]));
	}

	get startedTimeFilterActive(): boolean {
		return !!(this.startedTimeRange && (this.startedTimeRange[0] || this.startedTimeRange[1]));
	}

	onSortChange(column: string, order: 'ascend' | 'descend' | null | string): void {
		this.sortMap = {};
		if (order) {
			this.sortMap[column] = order as 'ascend' | 'descend';
		}
	}

	onScannerIdFilterChange(values: string[]): void {
		this.selectedScannerIds = values ?? [];
		this.dispatchQuery();
	}

	onBaseMaterialFilterChange(values: string[]): void {
		this.selectedBaseMaterialIds = values ?? [];
		this.dispatchQuery();
	}

	onStateFilterChange(values: string[]): void {
		this.selectedStates = values ?? [];
		this.dispatchQuery();
	}

	applyModifiedTimeFilter(): void {
		this.modifiedTimeFilterVisible = false;
		this.dispatchQuery();
	}

	resetModifiedTimeFilter(): void {
		this.modifiedTimeRange = null;
		this.modifiedTimeFilterVisible = false;
		this.dispatchQuery();
	}

	applyStartedTimeFilter(): void {
		this.startedTimeFilterVisible = false;
		this.dispatchQuery();
	}

	resetStartedTimeFilter(): void {
		this.startedTimeRange = null;
		this.startedTimeFilterVisible = false;
		this.dispatchQuery();
	}

	isNumericFilterActive(key: string): boolean {
		return this.numericFilters[key]?.value != null;
	}

	applyNumericFilter(key: string): void {
		this.numericFilterVisible[key] = false;
		this.dispatchQuery();
	}

	resetNumericFilter(key: string): void {
		this.numericFilters[key] = { operator: 'Equal', value: null, valueTo: null };
		this.numericFilterVisible[key] = false;
		this.dispatchQuery();
	}

	private queryTimeout: any;

	private buildQueryPayload(pageIndex: number, pageSize: number): BatchQueryPayload {
		const activeFilters: Record<string, string> = {};
		for (const [key, value] of Object.entries(this.filters)) {
			if (value.trim() !== '') {
				activeFilters[key] = value.trim();
			}
		}

		const multiFilters: Record<string, string[]> = {};
		if (this.selectedScannerIds.length) {
			multiFilters['scannerId'] = this.selectedScannerIds;
		}
		if (this.selectedBaseMaterialIds.length) {
			multiFilters['baseMaterial'] = this.selectedBaseMaterialIds;
		}
		if (this.selectedStates.length) {
			multiFilters['state'] = this.selectedStates;
		}

		const dateRangeFilters: Record<string, { from?: string; to?: string }> = {};
		if (this.modifiedTimeFilterActive) {
			dateRangeFilters['modifiedTime'] = {
				from: this.modifiedTimeRange?.[0]?.toISOString(),
				to: this.modifiedTimeRange?.[1]?.toISOString()
			};
		}
		if (this.startedTimeFilterActive) {
			dateRangeFilters['startedTime'] = {
				from: this.startedTimeRange?.[0]?.toISOString(),
				to: this.startedTimeRange?.[1]?.toISOString()
			};
		}

		const numericFilters: Record<string, { operator: NumericOperator; value: number; valueTo?: number }> = {};
		for (const key of this.numericColumns) {
			const f = this.numericFilters[key];
			if (f.value == null) continue;
			numericFilters[key] = f.operator === 'Between'
				? { operator: f.operator, value: f.value, valueTo: f.valueTo ?? f.value }
				: { operator: f.operator, value: f.value };
		}

		const sortKey = Object.keys(this.sortMap).find(key => this.sortMap[key]);
		const sortDirection = sortKey ? (this.sortMap[sortKey] === 'ascend' ? 1 : -1) : undefined;

		return {
			pageIndex,
			pageSize,
			filters: activeFilters,
			multiFilters,
			dateRangeFilters,
			numericFilters,
			sortKey,
			sortDirection
		};
	}

	dispatchQuery(): void {
		if (this.queryTimeout) {
			clearTimeout(this.queryTimeout);
		}
		this.queryTimeout = setTimeout(() => {
			this.state.queryPage(this.buildQueryPayload(1, this.pageSize));
		}, 400);
	}

	// Exports every row matching the current filter (not just the loaded page) — the backend
	// query is re-run with a high page size instead of dispatching into the paged table state.
	exportCsv(): void {
		if (this.isExporting) return;
		this.isExporting = true;
		const payload = this.buildQueryPayload(1, 100000);
		this.backend.queryBatchesPage(payload)
			.pipe(finalize(() => this.isExporting = false))
			.subscribe({
				next: result => this.downloadCsv(result.items),
				error: () => this.message.error($localize`Failed to export CSV`)
			});
	}

	private downloadCsv(batches: ProductionBatch[]): void {
		const headers = [
			$localize`#`, $localize`Status`, $localize`Scanner`, $localize`PO / Batch`, $localize`Color`,
			$localize`Base`, $localize`Producing`, $localize`Ordered`, $localize`Remaining`,
			$localize`Total Remaining`, $localize`Inspected`, $localize`Total Inspected`, $localize`Failed`,
			$localize`dEAvg`, $localize`dEMax`, $localize`CI`, $localize`Last Used`, $localize`Started`
		];

		const rows = batches.map(batch => [
			batch.inspectionSequence,
			this.poStateTranslation[batch.state],
			batch.scannerId,
			`${batch.poName} /${batch.sequenceInPo + 1}`,
			batch.color?.code ?? '',
			this.data[batch.baseMaterialId.toString()] || batch.baseMaterialId,
			batch.producingQuantity,
			batch.sequenceInPo > 0 ? `${batch.totalProducingQuantity}/${batch.orderedQuantity}` : batch.orderedQuantity,
			batch.remainingQuantity,
			batch.sequenceInPo > 0 ? batch.totalRemainingQuantity : '',
			batch.inspectedQuantity,
			batch.sequenceInPo > 0 ? batch.totalInspectedQuantity : '',
			batch.failedQuantity || '',
			batch.dEAvg ? batch.dEAvg.toFixed(2) : '',
			batch.dEMax ? batch.dEMax.toFixed(2) : '',
			batch.scans[0]?.ci ? batch.scans[0].ci.toFixed(2) : '',
			this.datePipe.transform(batch.modifiedTime, 'yyyy-MM-dd HH:mm:ss') ?? '',
			this.datePipe.transform(batch.startedTime, 'yyyy-MM-dd HH:mm:ss') ?? ''
		]);

		const csvLines = [headers, ...rows].map(row => row.map(cell => this.csvEscape(cell)).join(','));
		const csvContent = csvLines.join('\r\n');

		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const objectUrl = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = objectUrl;
		const timestamp = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
		a.download = `TCV-sort-filter-results-${timestamp}.CSV`;
		a.click();
		URL.revokeObjectURL(objectUrl);
	}

	private csvEscape(value: unknown): string {
		const str = value === null || value === undefined ? '' : String(value);
		return `"${str.replace(/"/g, '""')}"`;
	}

	trackByIndex(_: number, data: ProductionBatch): string {
		return data.id;
	}

	toColorTooltip(color: Color, mult: number): string {
		let res = mult * 3 + "kg - ";
		for (let i = 0; i < 26; i++) {
			let chr = String.fromCharCode(97 + i);
			if ((color as any)[chr]) {
				res += toUpper(chr) + ": " + ((color as any)[chr]) * mult + ", ";
			}
		}
		res = res.slice(0, -2);
		return res;
	}

	colorPicker(color: Lab): string {
		if (!color?.l) return "url(./assets/images/close.png)  center/contain";
		return `lab(${color?.l} ${color?.a} ${color?.b})`;
	}
}
